// The page's state: the platform profile it booted on, the session it holds,
// and the one connection a tenant's console or the identity plane keeps.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import { clearSession, loadSession, saveSession, type Session } from "../signin/tokens.ts";
import { Bus, type State } from "./bus.ts";
import { forgetMemberships, signInKey } from "./moves.ts";
import { Tab } from "./tab.ts";
import type { PlatformProfile, TenantProfile } from "./profile.ts";

export interface Page {
  origin: string;
  platform: PlatformProfile;
  session: Session | undefined;
  /** setSession keeps a session (signing in) or forgets it (signing out). */
  setSession: (s: Session | undefined) => void;
}

const PageContext = createContext<Page | undefined>(undefined);

export function PageProvider({
  origin,
  platform,
  children,
}: {
  origin: string;
  platform: PlatformProfile;
  children: ReactNode;
}) {
  const [session, setSessionState] = useState<Session | undefined>(() => loadSession());
  const value = useMemo<Page>(
    () => ({
      origin,
      platform,
      session,
      setSession: (s) => {
        // The memberships the workspace menu read belong to one sign-in: a
        // move between workspaces keeps them, a sign-out or another sign-in
        // forgets them (./moves.ts).
        if (s === undefined || session === undefined || signInKey(s) !== signInKey(session)) {
          forgetMemberships();
        }
        if (s === undefined) {
          // Signing out ends the tab's connection with the session.
          tab.release();
          clearSession();
        } else {
          saveSession(s);
        }
        setSessionState(s);
      },
    }),
    [origin, platform, session],
  );
  return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
}

export function usePage(): Page {
  const p = useContext(PageContext);
  if (p === undefined) {
    throw new Error("usePage outside the page");
  }
  return p;
}

/** Connection is a bus and where it stands. */
export interface Connection {
  bus: Bus;
  state: State;
  /** again opens a fresh connection: after a refusal, or when the session changed. */
  again: () => void;
}

/** tab is this browser tab's one connection (./tab.ts). */
export const tab = new Tab<Bus>(
  (origin, platform, tenant, lane, plane) => new Bus(origin, platform, tenant, lane, undefined, plane),
);

/**
 * useBus answers the tab's one connection for a tenant (or the identity plane
 * when null) on a lane; `plane` asks for the plane by naming `-` as the
 * tenant, as *All workspaces* and *Create a workspace* do (hq decision 0072). A mount, a remount or a re-render asking for what the
 * tab holds gets the same connection, connected once; it is not closed when a
 * component unmounts, only when the tab needs another or the person signs out.
 */
export function useBus(
  origin: string,
  platform: PlatformProfile,
  tenant: TenantProfile | null,
  lane: "oidc" | "github",
  plane = false,
): Connection {
  // Asking the tab at every render is what keeps it one: it answers the
  // connection it holds for the same workspace and opens nothing.
  const bus = tab.hold(origin, platform, tenant, lane, plane);
  // *Try again* replaces the held connection; the render after it reads the new one.
  const [, rerender] = useState(0);
  const subscribe = useCallback((tell: () => void) => bus.onChange(tell), [bus]);
  const state = useSyncExternalStore(subscribe, () => bus.current());
  return {
    bus,
    state,
    again: () => {
      tab.again();
      rerender((n) => n + 1);
    },
  };
}

const ConnectionContext = createContext<Connection | undefined>(undefined);

export function ConnectionProvider({ value, children }: { value: Connection; children: ReactNode }) {
  return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>;
}

/** useConnection is the tenant's connection a surface calls through. */
export function useConnection(): Connection {
  const c = useContext(ConnectionContext);
  if (c === undefined) {
    throw new Error("useConnection outside a connected page");
  }
  return c;
}
